import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    AddDefinitionField,
    AddTaskTestCase,
    AddTestCaseDisplayColumn,
    BatchDeleteParams,
    DefinitionField,
    DefinitionFieldList,
    DefinitionFieldVO,
    DisplayedColumnsParams,
    EditDefinitionField,
    EditTaskTestCase,
    EditTestCaseDisplayColumn,
    ExcelImport,
    ExcelImportField,
    SummaryData,
    TaskTestCase,
    TaskTestCaseForm,
    TaskTestCaseParams,
    TaskTestCaseWithPage,
    TestCaseBatchExport,
    TestCaseColumn,
    TestCaseExport,
    TestCaseImportResult,
    TestCaseParams,
    TestCaseReport,
    TestCaseStatistics,
} from '@app/interfaces/lm/task-test-case';
import { SelectOption } from '@app/interfaces/sys/dict';
import { STColumn } from '@app/interfaces/sys/st';
import { environment } from '@env/environment';
import { NzMessageService } from 'ng-zorro-antd/message';
import { lastValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TaskTestCaseService {
    private moduleApi = environment.api.services.lm;
    private baseUrl = this.moduleApi + '/test-cases';

    constructor(private http: HttpClient,
                private message: NzMessageService) {
    }

    async find(params: TaskTestCaseParams): Promise<TaskTestCaseWithPage> {
        return lastValueFrom(this.http.get<TaskTestCaseWithPage>(`${this.baseUrl}`, {
            params: Object.assign(params)
        }));
    }

    async findColumns(params: {type: string}): Promise<TestCaseColumn[]> {
        return lastValueFrom(this.http.get<TestCaseColumn[]>(`${this.moduleApi}/choose-column`, {
            params: Object.assign(params)
        }));
    }

    async addColumns(params: {request: AddTestCaseDisplayColumn[]}): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.moduleApi}/choose-column/batch`, params));
    }

    async editColumns(data: {request: EditTestCaseDisplayColumn[]}): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.moduleApi}/choose-column/batch`, data));
    }

    async edit(data: EditTaskTestCase): Promise<void> {
        return lastValueFrom(this.http.put<void>(this.baseUrl, data));
    }

    async add(params: AddTaskTestCase): Promise<TaskTestCase> {
        return lastValueFrom(this.http.post<TaskTestCase>(`${this.baseUrl}`, params));
    }

    async remove(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}`, {
            params: Object.assign({ id }),
        }));
    }

    async batchRemove(params: BatchDeleteParams): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/batch`, {
            body: Object.assign(params),
        }));
    }

    async excelParse(data: FormData): Promise<ExcelImport> {
        return lastValueFrom(this.http.post<ExcelImport>(`${this.moduleApi}/excel/parse`, data));
    }

    async excelParseLine(data: FormData): Promise<ExcelImport> {
        return lastValueFrom(this.http.post<ExcelImport>(`${this.moduleApi}/excel/parse/line-number`, data));
    }

    async batch(params: TestCaseBatchExport): Promise<TestCaseImportResult> {
        return lastValueFrom(this.http.post<TestCaseImportResult>(`${this.baseUrl}/batch`, params));
    }

    async getSmeOptions(params: {id: string}): Promise<string[]> {
        return lastValueFrom(this.http.get<string[]>(`${this.baseUrl}/find-sme`, {
            params: Object.assign(params)
        }));
    }

    async getSubCategory(params: {id: string}): Promise<string[]> {
        return lastValueFrom(this.http.get<string[]>(`${this.baseUrl}/find-sub-category`, {
            params: Object.assign(params)
        }));
    }

    async getCategory(params: {id: string}): Promise<string[]> {
        return lastValueFrom(this.http.get<string[]>(`${this.baseUrl}/find-category`, {
            params: Object.assign(params)
        }));
    }

    async getTestCaseDefinitionField(params: {page: number; size: number}): Promise<DefinitionFieldList> {
        return lastValueFrom(this.http.get<DefinitionFieldList>(`${this.baseUrl}/definition-field`, {
            params: Object.assign(params)
        }));
    }

    async AddTestCaseDefinitionField(params: AddDefinitionField): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.baseUrl}/definition-field`, params));
    }

    async TestCaseDefinitionFieldBatch(params: {
        testCaseDefinitionFieldList: AddDefinitionField[]
    }): Promise<DefinitionField[]> {
        return lastValueFrom(this.http.post<DefinitionField[]>(`${this.baseUrl}/definition-field/batch`, params));
    }

    async editTestCaseDefinitionField(data: EditDefinitionField): Promise<void> {
        return lastValueFrom(this.http.put<void>(`${this.baseUrl}/definition-field`, data));
    }

    async deleteTestCaseDefinitionField(id: string): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/definition-field`, {
            params: Object.assign({ id }),
        }));
    }

    async deleteDefinitionFieldBatch(ids: string[]): Promise<void> {
        return lastValueFrom(this.http.delete<void>(`${this.baseUrl}/definition-field/batch`, {
            params: Object.assign({ ids }),
        }));
    }

    async getStartDate(params: {id: string, timeZone: string}): Promise<string[]> {
        return lastValueFrom(this.http.get<string[]>(`${this.baseUrl}/find-start-date`, {
            params: Object.assign(params)
        }));
    }

    async getAcceptanceDate(params: {id: string, timeZone: string}): Promise<string[]> {
        return lastValueFrom(this.http.get<string[]>(`${this.baseUrl}/find-acceptance-date`, {
            params: Object.assign(params)
        }));
    }

    async getTestCaseReport(params: TestCaseParams): Promise<TestCaseReport[]> {
        return lastValueFrom(this.http.get<TestCaseReport[]>(`${this.baseUrl}/category`, {
            params: Object.assign(params)
        }));
    }

    async getCustomFilterOption(taskId: string, definitionFieldId: string): Promise<DefinitionFieldVO> {
        return lastValueFrom(this.http.get<DefinitionFieldVO>(`${this.baseUrl}/find-definition-field`, {
            params: Object.assign({
                id: taskId,
                definitionFieldId
            })
        }));
    }

    async statistics(params: TestCaseParams): Promise<TestCaseStatistics> {
        return lastValueFrom(this.http.get<TestCaseStatistics>(`${this.baseUrl}/statistics`, {
            params: Object.assign(params)
        }));
    }

    exportTestCase(params: {
        [key: string]: string | undefined
    }): void {
        let url = `${this.baseUrl}/export`;
        Object.keys(params)?.forEach((key, index) => {
            const value = params[key];
            if (value) {
                url = url + (index === 0 ? '?' : '&') + key + '=' + value;
            }
        });
        window.open(url);
    }

    async sendReport(data: {projectId?: string, projectGroupId?: string; recipients: string[], timeZone: string}): Promise<void> {
        return lastValueFrom(this.http.post<void>(`${this.moduleApi}/reporting/testcase/category`, data));
    }

    exportProject(id: string, status?: string): void {
        let url = `${this.baseUrl}/project/export?id=${id}`;
        if (status) {
            url = `${url}&status=${status}`;
        }
        window.open(url);
    }

    exportProjectGroup(id: string, status?: string): void {
        let url = `${this.baseUrl}/project-groups/export?id=${id}`;
        if (status) {
            url = `${url}&status=${status}`;
        }
        window.open(url);
    }

    getStatusOptions(): SelectOption[] {
        return [
            {
                label: 'Not Started',
                value: 'NOT_STARTED'
            },
            {
                label: 'Passed',
                value: 'PASSED'
            },
            {
                label: 'Failed',
                value: 'FAILED'
            },
            {
                label: 'Ongoing',
                value: 'ONGOING'
            },
            {
                label: 'N/A',
                value: 'NOT_APPLICABLE'
            }
        ];
    }

    formatStatus(status: string): string {
        const arr = this.getStatusOptions();
        const data = arr?.find((item) => item.value === status);
        return data ? data.label : '';
    }

    getSeverityOptions(): SelectOption[] {
        return [
            {
                label: 'Critical',
                value: 'CRITICAL'
            },
            {
                label: 'Major',
                value: 'MAJOR'
            },
            {
                label: 'Medium',
                value: 'MEDIUM'
            },
            {
                label: 'Minor',
                value: 'MINOR'
            }
        ];
    }

    formatSeverity(severity: string): string {
        const arr = this.getSeverityOptions();
        const data = arr?.find((item) => item.value === severity);
        return data ? data.label : '';
    }

    getTestCaseImportField(): ExcelImportField[] {
        return [
            {
                label: 'Test Case No',
                formControlName: 'no',
                nzRequired: true
            },
            {
                label: 'Category',
                formControlName: 'category',
                nzRequired: false
            },
            {
                label: 'Sub Category',
                formControlName: 'subCategory',
                nzRequired: false
            },
            {
                label: 'Name',
                formControlName: 'name',
                nzRequired: true
            },
            {
                label: 'Start Date',
                formControlName: 'startDate',
                nzRequired: false
            },
            {
                label: 'Acceptance Date',
                formControlName: 'acceptanceDate',
                nzRequired: false
            },
            {
                label: 'Description',
                formControlName: 'description',
                nzRequired: false
            },
            {
                label: 'Expected Result',
                formControlName: 'expectedResult',
                nzRequired: false
            },
            {
                label: 'Status',
                formControlName: 'status',
                nzRequired: false
            },
            {
                label: 'Telus SME Comments',
                formControlName: 'comments',
                nzRequired: false
            },
        ];
    }

    getProgressChartOptions(): SummaryData[] {
        return [
            {
                key: 'failed',   // back end
                name: 'Failed',  // front show
                status: 'FAILED',      // filter params
                value: 0,
                itemStyle: {
                    color: '#ffa39e',
                }
            },
            {
                key: 'passed',
                name: 'Passed',
                status: 'PASSED',
                value: 0,
                itemStyle: {
                    color: '#b7eb8f',
                }
            },
            {
                key: 'notApplicable',
                name: 'N/A',
                status: 'NOT_APPLICABLE',
                value: 0,
                itemStyle: {
                    color: '#ffd591',
                }
            },
            {
                key: 'notStarted',
                name: 'Not Started',
                status: 'NOT_STARTED',
                value: 0,
                itemStyle: {
                    color: 'gray',
                }
            },
            {
                key: 'ongoing',
                name: 'Ongoing',
                status: 'ONGOING',
                value: 0,
                itemStyle: {
                    color: '#91d5ff',
                }
            }
        ];
    }

    async editListColumns(type: string, arr: string[], displayedColumns: TestCaseColumn[]): Promise<void> {
        const currentDisplayedName = (displayedColumns || []).map(item => item.name);
        const addedFields = arr.filter(item => !currentDisplayedName.includes(item));
        if (addedFields && addedFields.length > 0) {
            const params = addedFields.map(name => {
                return {
                    type,
                    display: true,
                    name,
                };
            });
            await this.addColumns({
                request: params,
            });
        }
        if (addedFields.length !== arr.length) {
            const query: EditTestCaseDisplayColumn[] = (displayedColumns || []).map(item => {
                return {
                    display: (arr || []).includes(item.name as string),
                    id: item.id as string,
                };
            });
            await this.editColumns({
                request: query
            });
        }
        this.message.success(((displayedColumns || []).length > 0 ? 'Edit' : 'Add') + ' Columns successfully');
    }

    // 返回值---displayFields: check保存的数据, column: 格式化是否可见之后的数组, showFields: 可见字段的name-key
    async getCanDisplayedColumns(type: string, columns: STColumn[]): Promise<DisplayedColumnsParams> {
        const res = await this.findColumns({
            type
        });
        const displayedColumns = res;
        if ((res || []).length === 0) {
            return {
                displayedColumns,
                columns
            };
        }
        const showFields = (displayedColumns || []).filter(item => item.display).map(columnItem => columnItem.name) as string[];
        const arr = columns.map(column => {
            const fieldName = column.title as string;
            const isDisplay = showFields.includes(fieldName);
            return column.notShowInCheck ? {...column} : {
                ...column,
                iif: () => {
                    return isDisplay;
                }
            };
        });
        return {
            displayedColumns,
            columns: arr
        };
    }


    async getTableColumns(type: string, columns: STColumn[]): Promise<STColumn[]> {
        const res = await this.findColumns({
            type
        });
        const displayedColumns = res;
        const showFieldsKey: string[] = [];
        const columnMap: {
            [name: string]: TestCaseColumn
        } = {};
        (displayedColumns || []).forEach(it => {
            const { display, name, id } = it;
            columnMap[name] = it;
            if (!display) {
                return;
            }
            showFieldsKey.push(name);
        });
        const arr = columns.map(column => {
            const fieldName = column.title as string;
            const isDisplay = showFieldsKey.includes(fieldName);
            return column.notShowInCheck ? {...column} : {
                ...column,
                iif: () => {
                    return isDisplay;
                },
                apiColumnData: columnMap[fieldName]
            } as STColumn;
        });
        return arr;
    }
}
